import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useToast } from '../../../components/ui/ToastProvider'
import { getApiErrorMessage } from '../../../api/apiError'
import { ACTION_DECISION_TYPE_ID, type ActionFormRow } from '../../actions/types'
import { DECISION_TYPE, type MinuteItemKind, type MinuteItemRow } from '../../minuteItems/types'
import { downloadExcelTemplate } from '../../minutes/api/minutesApi'
import {
  ITEM_KIND_BY_SHEET,
  excelRowsToActionRows,
  excelRowsToItemRows,
} from '../../minutes/excelImport'
import { useExcelImport } from '../../minutes/hooks/useExcelImport'
import type { ExcelImportPreview, ExcelImportSheet, ExcelSheetKind } from '../../minutes/types'

/**
 * İçerik bölümünün karar tipi id'si → Excel sayfası. `KIND_BY_DECISION_TYPE`'ın (madde editörleri)
 * Excel karşılığı; **Kararlar bölümünün Excel sayfası yoktur** (088 kullanıcı kararı) ve bu yüzden
 * haritada da yer almaz → o bölüm doğal olarak "aktarılamaz" sayılır.
 */
const SHEET_BY_DECISION_TYPE: Record<number, ExcelSheetKind> = {
  [ACTION_DECISION_TYPE_ID]: 'ACTION',
  [DECISION_TYPE.AGENDA]: 'AGENDA',
  [DECISION_TYPE.PRINCIPLE]: 'PRINCIPLE',
}

interface Options {
  /**
   * Toplantı kimliği — Excel yükleme **denetim izinin** (503/504) hangi toplantıya ait olduğunu
   * belirler (093). Bu akışta toplantı zaten vardır; Tutanak Oluştur akışında yoktur ve iz bağlamsız
   * yazılır. Uç durumsuz kalır: bu parametre kalıcı kayıt yazdırmaz, yalnız izi bağlar.
   */
  meetingId?: number
  /** Toplantı tarihi (`YYYY-MM-DD`) — hedef teslim tarihi alt sınırı max(toplantı, bugün) olur. */
  meetingDate?: string
  /** Toplantının içerik bölümlerinin karar tipi id'leri (`REF_MEETING_DECISION_TYPE`). */
  sectionIds: number[]
  /** Aksiyon satırlarını mevcutların SONUNA ekler (çağıran anında-kaydet akışını tetikler). */
  onAppendActions: (rows: ActionFormRow[]) => void
  /** Madde satırlarını ilgili bölümün mevcutlarının SONUNA ekler. */
  onAppendItems: (kind: MinuteItemKind, rows: MinuteItemRow[]) => void
}

/**
 * **Toplantı Detay'da Excel ile toplu yükleme** (088'in ikinci kullanım yeri; kullanıcı isteği
 * 2026-08-19). Şablon indirme + dosya doğrulama + ön izleme durumu + aktarım burada toplanır; ekran
 * yalnız bölümü ve pop-up'ı render eder (`MeetingDetailPage` zaten uzun — mantık buraya alındı).
 *
 * **Tutanak Oluştur'dan tek davranış farkı: aktarım EKLER, değiştirmez** (kullanıcı kararı). Gerekçe:
 * orada kayıt henüz yoktur ve tekrar yükleme, önceki Excel satırlarının yerine geçer
 * (`replaceExcelRows`); burada maddeler **zaten kayıtlıdır** ve her değişiklik anında kaydedilir —
 * "değiştir" mevcut (elle girilmiş de olabilen) maddeleri silmek anlamına gelirdi. Bedeli bilinçle
 * kabul edilir: aynı dosya iki kez yüklenirse maddeler **çiftlenir**; pop-up bu yüzden kaç satırın
 * eklenaceğini yazar ve kullanıcı onaylar.
 *
 * **Doğrulama ucu ortaktır ve durumsuzdur** (`POST /minutes/excel-preview`): hiçbir kayıt yazmaz, bu
 * yüzden iki ekran onu değişiklik yapmadan paylaşır. Kalıcılaşma yolları farklıdır — orada `Yayınla`,
 * burada bölümlerin kendi anında-kaydet uçları.
 *
 * **Bölümü olmayan sayfa atlanır:** ekrandaki içerik bölümleri `REF_MEETING_DECISION_TYPE`'tan dinamik
 * gelir; bir toplantıda ör. Prensip bölümü yoksa o sayfanın satırlarının yazılacağı yer de yoktur.
 * Sessizce yutulmaz — kaç satırın atlandığı kullanıcıya bildirilir.
 */
export function useMeetingExcelImport({
  meetingId,
  meetingDate,
  sectionIds,
  onAppendActions,
  onAppendItems,
}: Options) {
  const { t } = useTranslation()
  const toast = useToast()
  const importMutation = useExcelImport()

  const [preview, setPreview] = useState<ExcelImportPreview | null>(null)
  const [templateDownloading, setTemplateDownloading] = useState(false)

  /** Bu toplantıda karşılığı olan Excel sayfaları (bölümü olmayan sayfa aktarılamaz). */
  const availableSheets = new Set(
    sectionIds
      .map((id) => SHEET_BY_DECISION_TYPE[id])
      .filter((kind): kind is ExcelSheetKind => kind != null),
  )

  /** Satır taşıyan ve bu toplantıya yazılabilen sayfalar. */
  const transferableSheets = (p: ExcelImportPreview | null): ExcelImportSheet[] =>
    (p?.sheets ?? []).filter((s) => s.rows.length > 0 && availableSheets.has(s.kind))

  const rowCount = (sheets: ExcelImportSheet[]) => sheets.reduce((n, s) => n + s.rows.length, 0)

  const onDownloadTemplate = async () => {
    setTemplateDownloading(true)
    try {
      await downloadExcelTemplate()
    } catch (err) {
      toast.error(getApiErrorMessage(err) ?? t('minutes.excel.uploadFailed'))
    } finally {
      setTemplateDownloading(false)
    }
  }

  const onFileSelected = (file: File) => {
    if (!file.name.toLowerCase().endsWith('.xlsx')) {
      toast.error(t('minutes.excel.onlyXlsx'))
      return
    }
    importMutation.mutate(
      // meetingId gönderilir: bu akışta toplantı VAR, dolayısıyla yükleme izi (503/504) o toplantıya
      // bağlanır. Tutanak Oluştur akışında toplantı olmadığı için orada gönderilmez (093).
      { file, meetingStartDate: meetingDate ?? null, meetingId },
      {
        onSuccess: setPreview,
        onError: (err) => toast.error(getApiErrorMessage(err) ?? t('minutes.excel.uploadFailed')),
      },
    )
  }

  /** Tek sayfayı ilgili bölümün sonuna ekler; kaydetmeyi çağıranın anında-kaydet akışı yapar. */
  const appendSheet = (sheet: ExcelImportSheet) => {
    if (sheet.kind === 'ACTION') {
      onAppendActions(excelRowsToActionRows(sheet.rows))
      return
    }
    const kind = ITEM_KIND_BY_SHEET[sheet.kind]
    onAppendItems(kind, excelRowsToItemRows(sheet.rows, kind))
  }

  const onTransfer = () => {
    if (!preview) return
    const sheets = transferableSheets(preview)
    // Atlanan = geçerli ama yazılacak bölümü olmayan satırlar (hatalı satırlar zaten `rows`'ta değil).
    const skipped = rowCount(preview.sheets.filter((s) => s.rows.length > 0)) - rowCount(sheets)

    sheets.forEach(appendSheet)
    setPreview(null)

    /*
      TOPLU BAŞARI TOAST'I YOK — bilinçli (bağımsız code review bulgusu 2026-08-19).

      İlk yazımda burada "N kayıt eklendi ve kaydedildi" atılıyordu; ama kaydetme bu satırda değil,
      bölümlerin kendi mutasyonlarında ASENKRON tamamlanıyor. Sunucu hata döndüğünde satırlar
      `committedRef`'e geri alınıyor ve ekranda önce yeşil "kaydedildi", ardından kırmızı
      "kaydedilemedi" çıkıyordu — yani hiçbir şey eklenmemişken kullanıcıya eklendiği söyleniyordu
      (deneyle üretildi). Çok sayfalı dosyada aynı mesaj KISMİ başarıyı da gizliyordu.

      Bölümlerin kendi anında-kaydet akışı zaten satır başına doğru sonucu bildiriyor
      ("… eklendi." / "… kaydedilemedi"), dolayısıyla ikinci bir özet mesaja gerek yok.
      Tutanak Oluştur'daki `transferred` metni bu sorunu taşımaz: orası "ekrana aktarıldı" der,
      kalıcılık iddiası yoktur — bu ekranda kalıcılık iddiası yeniydi.
    */
    // Atlanan satırlar sessizce kaybolmaz: kullanıcı neden eksik geldiğini görür.
    if (skipped > 0) {
      toast.error(t('minutes.excel.skippedNoSection', { count: skipped }))
    }
  }

  /**
   * Geçerli ama bu toplantıda yazılacak bölümü olmayan satır sayısı — pop-up bunu **buton pasifken de**
   * gösterir. Aksi hâlde tüm satırlar karşılığı olmayan bir bölüme aitse "Aktar" pasif kalır,
   * `onTransfer` hiç çalışmaz ve atlama gerekçesi kullanıcıya HİÇ söylenmezdi (code review bulgusu).
   */
  const skippedRowCount =
    rowCount((preview?.sheets ?? []).filter((s) => s.rows.length > 0)) -
    rowCount(transferableSheets(preview))

  return {
    preview,
    /**
     * Ön izlemede gösterilecek "kaç satır eklenecek" sayısı — `totalValidRows` atlanacakları da sayar
     * ve kullanıcıya fazla söz verirdi.
     */
    appendableRowCount: rowCount(transferableSheets(preview)),
    skippedRowCount,
    uploading: importMutation.isPending,
    templateDownloading,
    onDownloadTemplate,
    onFileSelected,
    onTransfer,
    onCancel: () => setPreview(null),
  }
}
